import { useState } from "react";
import { Navigate } from "react-router-dom";
import { useStudentAuth } from "@/frontend/contexts/StudentAuthContext";
import { Button } from "@/frontend/components/ui/button";
import { Input } from "@/frontend/components/ui/input";
import { Label } from "@/frontend/components/ui/label";
import { Card, CardContent, CardHeader } from "@/frontend/components/ui/card";
import { Loader2 } from "lucide-react";
import { useToast } from "@/backend/hooks/use-toast";

const StudentLogin = () => {
  const { user, isStudent, login } = useStudentAuth();
  const { toast } = useToast();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // If already logged in as student, redirect to portal
  if (user && isStudent) {
    return <Navigate to="/student" replace />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!email || !password) {
      const errorMsg = "Please enter both email and password";
      setError(errorMsg);
      toast({
        title: "Missing credentials",
        description: errorMsg,
        variant: "destructive",
      });
      return;
    }

    setSubmitting(true);
    try {
      await login(email, password);
      // Navigation will happen automatically via guard
    } catch (err) {
      const error = err as { code?: string };
      let errorMsg = "";

      if (error.code === 'auth/user-not-found' || error.code === 'auth/wrong-password') {
        errorMsg = "Invalid email or password";
      } else if (error.code === 'auth/invalid-email') {
        errorMsg = "Invalid email format";
      } else if (error.code === 'auth/too-many-requests') {
        errorMsg = "Too many failed attempts. Please try again later.";
      } else {
        errorMsg = "An error occurred. Please try again.";
      }

      setError(errorMsg);
      toast({
        title: "Login failed",
        description: errorMsg,
        variant: "destructive",
      });
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-muted flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center pb-2">
          <h1 className="text-2xl font-bold text-primary">HOME2STUDENTS</h1>
          <p className="text-muted-foreground text-sm mt-1">Student Portal</p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="student@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={submitting}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={submitting}
              />
            </div>

            {error && (
              <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-lg">
                {error}
              </div>
            )}

            <Button type="submit" className="w-full" disabled={submitting}>
              {submitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Signing in...
                </>
              ) : (
                "Sign In"
              )}
            </Button>

            <p className="text-xs text-muted-foreground text-center mt-4">
              Use the credentials provided by your residence administrator
            </p>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default StudentLogin;
